import { useEffect, useRef } from 'react';

const benefits = [
  { icon: 'ri-leaf-line', title: 'Enerji Tasarrufu', desc: 'Düzenli bakım ile doğalgaz tüketiminizi %15\'e kadar azaltın.' },
  { icon: 'ri-shield-check-line', title: 'Güvenli Kullanım', desc: 'Gaz kaçağı ve yanma problemleri erken tespit edilir, güvenliğiniz sağlanır.' },
  { icon: 'ri-time-line', title: 'Uzun Ömür', desc: 'Periyodik bakım, cihazınızın kullanım ömrünü önemli ölçüde uzatır.' },
  { icon: 'ri-tools-line', title: 'Arıza Önleme', desc: 'Beklenmedik arızaların önüne geçilir, ani maliyet sürprizleri yaşanmaz.' },
  { icon: 'ri-bar-chart-line', title: 'Yüksek Performans', desc: 'Brülör, fan, eşanjör ve filtreler kontrol edilerek verimlilik artırılır.' },
  { icon: 'ri-money-cny-circle-line', title: 'Ekonomik Çözüm', desc: 'Küçük bakım maliyeti, büyük onarım masraflarının önüne geçer.' },
];

export default function MaintenanceSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('opacity-100', 'translate-y-0');
          e.target.classList.remove('opacity-0', 'translate-y-6');
        }
      }),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="maintenance" ref={ref} className="w-full py-16 md:py-24 bg-[#f0f7ff]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-primary bg-white border border-[#dbeafe] px-3 py-1.5 rounded-full uppercase tracking-widest mb-4">
            Kombi Bakımı
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">
            Düzenli Bakımın Faydaları
          </h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">
            Uzmanlar, kombi bakımının yılda en az bir kez — özellikle kış sezonu başlamadan önce — yapılmasını önermektedir. Düzenli bakım, kombinin yoğun kullanıldığı dönemlerde sorunsuz çalışmasını sağlar.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {benefits.map((b, i) => (
            <div
              key={b.title}
              className="anim opacity-0 translate-y-6 transition-all duration-500 group flex gap-4 p-6 bg-white rounded-2xl border border-slate-100 hover:border-primary/25 hover:-translate-y-0.5 transition-all"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center bg-primary/[0.06] text-primary rounded-xl flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <i className={`${b.icon} text-lg`} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-primary transition-colors">{b.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Image + info box */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-[320px] flex-shrink-0">
            <div className="relative w-full h-[260px] md:h-full md:min-h-[280px] bg-white rounded-2xl overflow-hidden border border-[#dbeafe]">
              <img
                src="/images/maintenance-tech-01.jpg"
                alt="Kombi Bakımı"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* Extra boiler close-up image */}
          <div className="hidden lg:block w-[200px] flex-shrink-0">
            <div className="relative w-full h-full min-h-[280px] bg-white rounded-2xl overflow-hidden border border-[#dbeafe]">
              <img
                src="/images/boiler-closeup-parts-v1.jpg"
                alt="Kombi İç Parçaları"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                <p className="text-white text-xs font-semibold">Orijinal Yedek Parça</p>
                <p className="text-white/70 text-[11px]">Garantili parça ile onarım</p>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-white rounded-2xl border border-slate-100 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-14 h-14 flex items-center justify-center bg-[#f0f7ff] text-primary rounded-2xl flex-shrink-0">
              <i className="ri-calendar-check-line text-2xl" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900 mb-1">Kombi Bakımı Ne Sıklıkla Yapılmalı?</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                Yılda en az bir kez, tercihen Ekim–Kasım aylarında kış öncesi bakım yaptırmanız önerilir. Kombinin bulunduğu ortam şartları, kullanım sıklığı ve cihazın yaşı bakım periyodunu etkileyebilir. Teknik servis kontrolünde kişiselleştirilmiş bakım planı oluşturulması en doğru yaklaşımdır.
              </p>
              <a
                href="tel:05332836679"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-fill" />
                Bakım Randevusu Al
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}